import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Modal } from '../shared';
import { useProfiles, usePlatformAdmin } from '../../hooks';
import { useStore } from '../../store';
import { ProfileList } from './ProfileList';
import { CreateProfileDialog } from './CreateProfileDialog';
import { EditProfileDialog } from './EditProfileDialog';
import { ProfileMappingsManager } from './ProfileMappingsManager';
import { OnDemandAvailability } from './OnDemandAvailability';
import type { Profile, ProfileCreateReq, ProfileUpdateReq } from '@interlucent/organization-protocol';
import { getApiBaseUrl } from '../../lib/preview-context';
import styles from './ProfilesTab.module.css';

interface ProfilesTabProps {
  projectId: string;
}

export function ProfilesTab({ projectId }: ProfilesTabProps) {
  const {
    profiles,
    createProfile,
    updateProfile,
    deleteProfile,
    toggleProfileStatus
  } = useProfiles(projectId);
  const isConnected = useStore(s => s.connectionState === 'connected');
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();

  // Fetch infrastructure defaults for platform admins
  const [infraDefaults, setInfraDefaults] = useState<unknown>(null);
  useEffect(() => {
    if (!isPlatformAdmin) return;
    (async () => {
      try {
        const token = await getToken({ template: 'default' });
        if (!token) return;
        const res = await fetch(`${getApiBaseUrl()}/api/admin/pool-infra-defaults`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setInfraDefaults((data as any).defaults);
        }
      } catch { /* non-critical */ }
    })();
  }, [isPlatformAdmin, getToken]);

  // Filter to dedicated pools only (private_pool)
  const dedicatedPools = useMemo(() => {
    return profiles.filter(p => p.type === 'private_pool');
  }, [profiles]);

  // Dialog states
  const [activeTab, setActiveTab] = useState<'on-demand' | 'dedicated'>('on-demand');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [managingProfile, setManagingProfile] = useState<Profile | null>(null);
  const [deletingProfile, setDeletingProfile] = useState<Profile | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create handlers
  const handleOpenCreate = useCallback(() => {
    setIsCreateOpen(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setIsCreateOpen(false);
  }, []);

  const handleCreate = useCallback(async (data: Omit<ProfileCreateReq, 'projectId'>) => {
    return await createProfile(data);
  }, [createProfile]);

  // Edit handlers
  const handleOpenEdit = useCallback((profile: Profile) => {
    setEditingProfile(profile);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditingProfile(null);
  }, []);

  const handleUpdate = useCallback(async (profileId: string, data: Omit<ProfileUpdateReq, 'id'>) => {
    return await updateProfile(profileId, data);
  }, [updateProfile]);

  // Toggle status handler
  const handleToggleStatus = useCallback(async (profileId: string, newStatus: boolean) => {
    await toggleProfileStatus(profileId, newStatus);
  }, [toggleProfileStatus]);

  // Mappings handlers
  const handleOpenMappings = useCallback((profileId: string) => {
    const profile = profiles.find(p => p.id === profileId);
    if (profile) {
      setManagingProfile(profile);
    }
  }, [profiles]);

  const handleCloseMappings = useCallback(() => {
    setManagingProfile(null);
  }, []);

  // Delete handlers
  const handleOpenDelete = useCallback((profileId: string) => {
    const profile = profiles.find(p => p.id === profileId);
    if (profile) {
      setDeletingProfile(profile);
      setDeleteError(null);
    }
  }, [profiles]);

  const handleCloseDelete = useCallback(() => {
    if (!isDeleting) {
      setDeletingProfile(null);
      setDeleteError(null);
    }
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingProfile) return;

    setIsDeleting(true);
    setDeleteError(null);

    const result = await deleteProfile(deletingProfile.id);

    if (result.success) {
      setDeletingProfile(null);
    } else {
      setDeleteError(result.error || 'Failed to delete profile');
    }

    setIsDeleting(false);
  }, [deletingProfile, deleteProfile]);

  return (
    <div className={styles.container}>
      <div className={styles.subTabBar}>
        <div className={styles.subTabList}>
          <button
            type="button"
            className={activeTab === 'on-demand' ? styles.subTabActive : styles.subTab}
            onClick={() => setActiveTab('on-demand')}
          >
            On-Demand
          </button>
          <button
            type="button"
            className={activeTab === 'dedicated' ? styles.subTabActive : styles.subTab}
            onClick={() => setActiveTab('dedicated')}
          >
            Dedicated Pools
          </button>
        </div>
        {activeTab === 'dedicated' && (
          <div className={styles.subTabActions}>
            <button
              onClick={handleOpenCreate}
              className={styles.createButton}
              disabled={!isConnected}
            >
              Create Pool
            </button>
          </div>
        )}
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'on-demand' && (
          <OnDemandAvailability projectId={projectId} />
        )}
        {activeTab === 'dedicated' && (
          <ProfileList
            projectId={projectId}
            profiles={dedicatedPools}
            onEdit={handleOpenEdit}
            onDelete={handleOpenDelete}
            onToggleStatus={handleToggleStatus}
            onManageMappings={handleOpenMappings}
          />
        )}
      </div>

      <CreateProfileDialog
        isOpen={isCreateOpen}
        projectId={projectId}
        isPlatformAdmin={isPlatformAdmin}
        infraDefaults={infraDefaults}
        onClose={handleCloseCreate}
        onCreate={handleCreate}
      />

      {editingProfile && (
        <EditProfileDialog
          isOpen={true}
          profile={editingProfile}
          isPlatformAdmin={isPlatformAdmin}
          infraDefaults={infraDefaults}
          onClose={handleCloseEdit}
          onUpdate={handleUpdate}
        />
      )}

      {managingProfile && (
        <ProfileMappingsManager
          isOpen={true}
          profileId={managingProfile.id}
          profileName={managingProfile.name}
          projectId={projectId}
          onClose={handleCloseMappings}
        />
      )}

      {deletingProfile && (
        <Modal
          isOpen={true}
          title="Delete Dedicated Pool"
          subtitle={<span>Are you sure you want to delete <strong>{deletingProfile.name}</strong>?</span>}
          onClose={handleCloseDelete}
          size="small"
        >
          <div className={styles.deleteContent}>
            <p className={styles.deleteWarning}>
              This action cannot be undone. All application mappings associated with this pool will also be removed.
            </p>

            {deleteError && (
              <div className={styles.deleteError}>{deleteError}</div>
            )}

            <div className={styles.deleteActions}>
              <button
                onClick={handleCloseDelete}
                disabled={isDeleting}
                className={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className={styles.deleteButton}
              >
                {isDeleting ? 'Deleting...' : 'Delete Pool'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
